import React, { useState } from "react";
import {
  Box,
  Typography,
  Fade,
  Card,
  CardContent,
  Button,
  TextField,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TableContainer,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Tooltip,
  IconButton,
  Chip,
  Pagination,
  Skeleton,
  Collapse,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import { ContentCopy, Print, FilterList, FilterListOff } from "@mui/icons-material";
import { Search, Filter, Download, Lock, Trash2, KeyRound } from "lucide-react";
import { ColumnVisibilityButton } from "../../shared/components/ColumnVisibilityButton";
import { SortableHeader } from "../../shared/components/SortableHeader";
import { MobileSortControl } from "../../shared/components/MobileSortControl";
import { PageHeader } from "../../shared/components/PageHeader";
import { EmptyState } from "../../shared/components/EmptyState";
import { getSystemDetails, formatToIST } from "../../utils/formatting";
import { searchOptions, getLicenseExpiryStatus } from "../../utils/licenseStatus";
import { getStatusColorSx } from "../../theme/theme";

function LicensesView({
  darkMode,
  theme,
  licenses,
  systems,
  searchTerm,
  handleSearch,
  searchType,
  setSearchType,
  sortConfig,
  handleSort,
  visibleColumns,
  setVisibleColumns,
  toggleColumn,
  columnMenuAnchor,
  activeColumnTable,
  handleColumnMenuOpen,
  handleColumnMenuClose,
  page,
  handlePageChange,
  rowsPerPage,
  isMobile,
  loading,
  filteredLicenses,
  paginatedLicenses,
  copiedField,
  handleCopy,
  handleSystemHover,
  handleSystemHoverClose,
  handleOpenDialog,
  handlePrint,
  downloadLicenseFile,
  generateOfflineKey,
  deleteLicense,
  splitScreen,
  currentUser,
}) {
  const [showFilters, setShowFilters] = useState(true);

  return (
    <Fade in={true} timeout={500}>
      <Box key="licenses-view">
        <PageHeader
          title="License Management"
          subtitle="Manage and monitor all software licenses"
          darkMode={darkMode}
          actions={
            <>
              <Tooltip title={showFilters ? "Hide Filters" : "Show Filters"}>
                <Button
                  variant="outlined"
                  size="small"
                  color={showFilters ? "primary" : "inherit"}
                  startIcon={showFilters ? <FilterListOff fontSize="small" /> : <FilterList fontSize="small" />}
                  onClick={() => setShowFilters(!showFilters)}
                >
                  Filters
                </Button>
              </Tooltip>
              <Button
                variant="outlined"
                size="small"
                startIcon={<Print />}
                onClick={() => handlePrint('licenses')}
              >
                Print
              </Button>
              <Button
                variant="contained"
                startIcon={<KeyRound size={18} />}
                onClick={() => handleOpenDialog("license")}
                sx={{ px: 4, py: 1.5 }}
              >
                Add New License
              </Button>
              <ColumnVisibilityButton
                tableType="licenses"
                visibleColumns={visibleColumns}
                setVisibleColumns={setVisibleColumns}
                toggleColumn={toggleColumn}
                columnMenuAnchor={columnMenuAnchor}
                activeColumnTable={activeColumnTable}
                handleColumnMenuOpen={handleColumnMenuOpen}
                handleColumnMenuClose={handleColumnMenuClose}
                theme={theme}
              />
            </>
          }
        />

        <Collapse in={showFilters}>
          <Card sx={{ mb: 3, bgcolor: darkMode ? '#0E1626' : 'background.paper' }}>
            <CardContent sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1 }}>
                  <Filter size={20} color={darkMode ? '#93A2BC' : theme.palette.text.secondary} />
                  <Typography variant="body2" sx={{ color: darkMode ? '#93A2BC' : 'text.secondary', fontWeight: 500 }}>
                    Filter by:
                  </Typography>
                </Box>
                <TextField
                  variant="outlined"
                  placeholder={`Search by ${searchOptions.licenses.find(o => o.value === searchType)?.label || 'License ID'}`}
                  value={searchTerm}
                  onChange={handleSearch}
                  size="small"
                  sx={{ minWidth: 300 }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Search size={18} />
                      </InputAdornment>
                    ),
                  }}
                />
                <FormControl size="small" sx={{ minWidth: 180 }}>
                  <InputLabel>Search By</InputLabel>
                  <Select
                    value={searchType}
                    onChange={(e) => setSearchType(e.target.value)}
                    label="Search By"
                  >
                    {searchOptions.licenses.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>
            </CardContent>
          </Card>
        </Collapse>

        <Box sx={splitScreen ? { display: 'flex', gap: 2 } : {}}>
        {loading.licenses ? (
          <Card sx={{ bgcolor: darkMode ? '#0E1626' : 'background.paper', flex: splitScreen ? '0 0 60%' : undefined }}>
            {isMobile ? (
              <CardContent>
                {[...Array(4)].map((_, i) => <Skeleton key={i} variant="rounded" height={90} sx={{ mb: 1.5 }} />)}
              </CardContent>
            ) : (
              <TableContainer>
                <Table sx={{ minWidth: 1200 }}>
                  <TableBody>
                    {[...Array(5)].map((_, i) => (
                      <TableRow key={i}>
                        {[...Array(8)].map((__, j) => (
                          <TableCell key={j}><Skeleton variant="text" /></TableCell>
                        ))}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Card>
        ) : isMobile ? (
          <Card sx={{ bgcolor: darkMode ? '#0E1626' : 'background.paper' }}>
            <CardContent sx={{ p: 2 }}>
              <MobileSortControl
                tableType="licenses"
                currentConfig={sortConfig.licenses}
                handleSort={handleSort}
                options={[
                  { value: 'license_id', label: 'License ID' },
                  { value: 'uuid', label: 'System UID' },
                  { value: 'project_id', label: 'Project' },
                  { value: 'software_id', label: 'Software' },
                  { value: 'start_date', label: 'Start Date' },
                  { value: 'end_date', label: 'End Date' },
                  { value: 'registered_at', label: 'Registered At' },
                  { value: 'status', label: 'Status' },
                ]}
              />
              {paginatedLicenses.map((license) => {
                const systemDetails = getSystemDetails(systems, license.uuid);
                const status = getLicenseExpiryStatus(license.end_date);
                return (
                  <Box
                    key={license.license_id}
                    sx={{
                      p: 2, mb: 1.5, borderRadius: 2,
                      bgcolor: darkMode ? '#16213A' : '#EAF3FB',
                      border: darkMode ? '1px solid #24304A' : '1px solid #D8E1E8',
                    }}
                  >
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                      <Typography variant="body2" sx={{ fontWeight: 700, fontFamily: 'monospace', color: darkMode ? '#EAF3FB' : '#0E1B2C' }}>
                        {license.license_id}
                      </Typography>
                      <Chip size="small" label={status === 'expired' ? 'Expired' : status === 'expiring' ? 'Expiring' : 'Active'} sx={getStatusColorSx(status, darkMode)} />
                    </Box>
                    <Typography variant="caption" sx={{ display: 'block', color: darkMode ? '#93A2BC' : 'text.secondary', fontFamily: 'monospace', mb: 1 }}>
                      {license.uuid?.substring(0, 16)}...
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 1.5 }}>
                      <Chip size="small" label={systemDetails?.project_id || 'N/A'} sx={{ bgcolor: alpha(theme.palette.primary.main, 0.1), color: 'primary.main', fontWeight: 600 }} />
                      <Chip size="small" label={systemDetails?.software_id || 'N/A'} sx={{ bgcolor: alpha(theme.palette.secondary.main, 0.1), color: 'secondary.main', fontWeight: 600 }} />
                    </Box>
                    <Typography variant="caption" sx={{ display: 'block', color: darkMode ? '#93A2BC' : 'text.secondary', mb: 1.5 }}>
                      {license.start_date} → {license.end_date}
                    </Typography>
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 0.5 }}>
                      {currentUser === "admin" && (
                        <IconButton color="primary" onClick={() => downloadLicenseFile(license.license_id)} size="small">
                          <Download size={18} />
                        </IconButton>
                      )}
                      {currentUser === "admin" && (
                        <IconButton color="secondary" onClick={() => generateOfflineKey(license.license_id)} size="small">
                          <Lock size={18} />
                        </IconButton>
                      )}
                      <IconButton color="error" onClick={() => deleteLicense(license.license_id)} size="small">
                        <Trash2 size={18} />
                      </IconButton>
                    </Box>
                  </Box>
                );
              })}
              {filteredLicenses.length === 0 && (
                <EmptyState darkMode={darkMode} title="No licenses found" subtitle="Try adjusting your search or add a new license" />
              )}
              {filteredLicenses.length > rowsPerPage && (
                <Box sx={{ display: 'flex', justifyContent: 'center', pt: 2 }}>
                  <Pagination count={Math.ceil(filteredLicenses.length / rowsPerPage)} page={page} onChange={handlePageChange} color="primary" size="medium" />
                </Box>
              )}
            </CardContent>
          </Card>
        ) : (
          <Card sx={{ bgcolor: darkMode ? '#0E1626' : 'background.paper' }}>
            <TableContainer sx={{ maxHeight: 600 }}>
              <Table stickyHeader sx={{ minWidth: 1200 }}>
                <TableHead>
                  <TableRow>
                    {visibleColumns.licenses.license_id && (
                      <SortableHeader
                        label="License ID"
                        tableType="licenses"
                        columnKey="license_id"
                        currentConfig={sortConfig.licenses}
                        handleSort={handleSort}
                      />
                    )}
                    {visibleColumns.licenses.uuid && (
                      <SortableHeader
                        label="System UID"
                        tableType="licenses"
                        columnKey="uuid"
                        currentConfig={sortConfig.licenses}
                        handleSort={handleSort}
                      />
                    )}
                    {visibleColumns.licenses.project_id && (
                      <SortableHeader
                        label="Project"
                        tableType="licenses"
                        columnKey="project_id"
                        currentConfig={sortConfig.licenses}
                        handleSort={handleSort}
                      />
                    )}
                    {visibleColumns.licenses.software_id && (
                      <SortableHeader
                        label="Software"
                        tableType="licenses"
                        columnKey="software_id"
                        currentConfig={sortConfig.licenses}
                        handleSort={handleSort}
                      />
                    )}
                    {visibleColumns.licenses.start_date && (
                      <SortableHeader
                        label="Start Date"
                        tableType="licenses"
                        columnKey="start_date"
                        currentConfig={sortConfig.licenses}
                        handleSort={handleSort}
                      />
                    )}
                    {visibleColumns.licenses.end_date && (
                      <SortableHeader
                        label="End Date"
                        tableType="licenses"
                        columnKey="end_date"
                        currentConfig={sortConfig.licenses}
                        handleSort={handleSort}
                      />
                    )}
                    {visibleColumns.licenses.registered_at && (
                      <SortableHeader
                        label="Registered At"
                        tableType="licenses"
                        columnKey="registered_at"
                        currentConfig={sortConfig.licenses}
                        handleSort={handleSort}
                      />
                    )}
                    {visibleColumns.licenses.status && (
                      <SortableHeader
                        label="Status"
                        tableType="licenses"
                        columnKey="status"
                        currentConfig={sortConfig.licenses}
                        handleSort={handleSort}
                      />
                    )}
                    {visibleColumns.licenses.actions && (
                      <TableCell align="center">Actions</TableCell>
                    )}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {paginatedLicenses.map((license) => {
                    const systemDetails = getSystemDetails(systems, license.uuid);
                    const status = getLicenseExpiryStatus(license.end_date);

                    return (
                      <TableRow key={license.license_id} hover>
                        {visibleColumns.licenses.license_id && (
                          <TableCell>
                            <Tooltip title={copiedField === license.license_id ? "Copied!" : "Click to copy"}>
                              <Box
                                onClick={() => handleCopy(license.license_id, license.license_id)}
                                sx={{ display: 'flex', alignItems: 'center', gap: 0.5, cursor: 'pointer' }}
                              >
                                <Typography variant="body2" sx={{ fontWeight: 600, fontFamily: 'monospace', color: darkMode ? '#EAF3FB' : '#0E1B2C' }}>
                                  {license.license_id}
                                </Typography>
                                <ContentCopy sx={{ fontSize: 14, opacity: 0.5, color: copiedField === license.license_id ? 'success.main' : 'inherit' }} />
                              </Box>
                            </Tooltip>
                          </TableCell>
                        )}
                        {visibleColumns.licenses.uuid && (
                          <TableCell>
                            <Tooltip title={copiedField === `lic-uuid-${license.license_id}` ? "Copied!" : ""}>
                              <Box
                                onClick={() => handleCopy(license.uuid, `lic-uuid-${license.license_id}`)}
                                onMouseEnter={(e) => handleSystemHover(e, license.uuid)}
                                onMouseLeave={handleSystemHoverClose}
                                sx={{ display: 'flex', alignItems: 'center', gap: 0.5, cursor: 'pointer' }}
                              >
                                <Typography variant="body2" sx={{ fontFamily: 'monospace', color: darkMode ? '#93A2BC' : 'text.secondary' }}>
                                  {license.uuid.substring(0, 8)}...
                                </Typography>
                                <ContentCopy sx={{ fontSize: 14, opacity: 0.5, color: copiedField === `lic-uuid-${license.license_id}` ? 'success.main' : 'inherit' }} />
                              </Box>
                            </Tooltip>
                          </TableCell>
                        )}
                        {visibleColumns.licenses.project_id && (
                          <TableCell>
                            <Chip
                              size="small"
                              label={systemDetails?.project_id || 'N/A'}
                              sx={{
                                bgcolor: alpha(theme.palette.primary.main, 0.1),
                                color: 'primary.main',
                                fontWeight: 600,
                              }}
                            />
                          </TableCell>
                        )}
                        {visibleColumns.licenses.software_id && (
                          <TableCell>
                            <Chip
                              size="small"
                              label={systemDetails?.software_id || 'N/A'}
                              sx={{
                                bgcolor: alpha(theme.palette.secondary.main, 0.1),
                                color: 'secondary.main',
                                fontWeight: 600,
                              }}
                            />
                          </TableCell>
                        )}
                        {visibleColumns.licenses.start_date && (
                          <TableCell>
                            <Typography variant="body2" sx={{ color: darkMode ? '#EAF3FB' : '#0E1B2C' }}>
                              {license.start_date}
                            </Typography>
                          </TableCell>
                        )}
                        {visibleColumns.licenses.end_date && (
                          <TableCell>
                            <Typography variant="body2" sx={{ color: darkMode ? '#EAF3FB' : '#0E1B2C' }}>
                              {license.end_date}
                            </Typography>
                          </TableCell>
                        )}
                        {visibleColumns.licenses.registered_at && (
                          <TableCell>
                            <Typography variant="caption" sx={{ color: darkMode ? '#93A2BC' : 'text.secondary' }}>
                              {formatToIST(license.registered_at)}
                            </Typography>
                          </TableCell>
                        )}
                        {visibleColumns.licenses.status && (
                          <TableCell>
                            <Chip
                              label={status === 'expired' ? "Expired" : status === 'expiring' ? "Expiring Soon" : "Active"}
                              size="small"
                              sx={getStatusColorSx(status, darkMode)}
                            />
                          </TableCell>
                        )}
                        {visibleColumns.licenses.actions && (
                          <TableCell align="center">
                            {currentUser === "admin" && (
                              <Tooltip title="Download .lic File">
                                <IconButton
                                  color="primary"
                                  onClick={() => downloadLicenseFile(license.license_id)}
                                  size="small"
                                  sx={{ mr: 1 }}
                                >
                                  <Download size={18} />
                                </IconButton>
                              </Tooltip>
                            )}
                            {currentUser === "admin" && (
                              <Tooltip title="Generate Offline Activation Key">
                                <IconButton
                                  color="secondary"
                                  onClick={() => generateOfflineKey(license.license_id)}
                                  size="small"
                                  sx={{ mr: 1 }}
                                >
                                  <Lock size={18} />
                                </IconButton>
                              </Tooltip>
                            )}
                            <Tooltip title="Delete License">
                              <IconButton
                                color="error"
                                onClick={() => deleteLicense(license.license_id)}
                                size="small"
                              >
                                <Trash2 size={18} />
                              </IconButton>
                            </Tooltip>
                          </TableCell>
                        )}
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>

            {filteredLicenses.length > rowsPerPage && (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                <Pagination
                  count={Math.ceil(filteredLicenses.length / rowsPerPage)}
                  page={page}
                  onChange={handlePageChange}
                  color="primary"
                  size={isMobile ? 'medium' : 'large'}
                  showFirstButton
                  showLastButton
                />
              </Box>
            )}

            {filteredLicenses.length === 0 && (
              <EmptyState darkMode={darkMode} title="No licenses found" subtitle="Try adjusting your search or add a new license" />
            )}
          </Card>
        )}
        {splitScreen && (
          <Card sx={{ flex: '0 0 40%', bgcolor: darkMode ? '#0E1626' : 'background.paper', maxHeight: 'calc(100vh - 200px)', overflowY: 'auto' }}>
            <CardContent sx={{ p: 2 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 2, color: darkMode ? '#EAF3FB' : '#0E1B2C' }}>
                Systems Overview
              </Typography>
              {systems.map((sys) => {
                const activeLic = licenses.find(l => l.uuid === sys.uuid && new Date(l.end_date) >= new Date());
                return (
                  <Box key={sys.uuid} sx={{ mb: 1.5, p: 1.5, borderRadius: 2, bgcolor: darkMode ? '#16213A' : '#EAF3FB', border: darkMode ? '1px solid #24304A' : '1px solid #D8E1E8' }}>
                    <Typography variant="caption" sx={{ fontFamily: 'monospace', fontWeight: 600, color: darkMode ? '#EAF3FB' : '#0E1B2C', display: 'block' }}>
                      {sys.uuid.substring(0, 16)}...
                    </Typography>
                    <Typography variant="caption" sx={{ color: activeLic ? 'success.main' : 'error.main', display: 'block' }}>
                      {activeLic ? `${activeLic.license_id} • exp: ${activeLic.end_date}` : 'No active license'}
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 0.5, mt: 0.5 }}>
                      <Chip size="small" label={sys.amc ? 'AMC Active' : 'No AMC'} sx={{ height: 18, fontSize: '0.65rem', bgcolor: sys.amc ? alpha(theme.palette.success.main, 0.1) : alpha(theme.palette.grey[500], 0.1), color: sys.amc ? 'success.main' : 'text.secondary' }} />
                      {sys.project_id && <Chip size="small" label={sys.project_id} sx={{ height: 18, fontSize: '0.65rem', bgcolor: alpha(theme.palette.primary.main, 0.1), color: 'primary.main' }} />}
                    </Box>
                  </Box>
                );
              })}
            </CardContent>
          </Card>
        )}
        </Box>
      </Box>
    </Fade>
  );
}

export default LicensesView;
